import { customInputProps } from '@/app/entrar/customInputProps';
import { FloatingLabelTextInput } from '@/components/FloatingLabelComponents/FloatingLabelInput';
import PhoneInputWithLabel from '@/components/PhoneInputWithLabel';
import useApi from '@/hooks/useApi/useApi';
import { Alert, Button, Group, Text, Title } from '@mantine/core';
import { useForm } from '@mantine/form';
import { notifications } from '@mantine/notifications';
import { IconArrowLeft } from '@tabler/icons-react';
import Link from 'next/link';
import { useRouter, useSearchParams } from 'next/navigation';
import { ReactNode, useEffect, useState } from 'react';
import NewPassword from './NewPassword';

export type ResetPasswordForm = {
  email?: string;
  phoneNumber?: string;
};

export type Response = {
  success: boolean;
  message: string;
  lastNumbers: string;
  hiddenEmail: string;
  code: string;
};

export default function ForgotPassword() {
  const searchParams = useSearchParams();

  const router = useRouter();

  const email = searchParams?.get('email');

  const [stepVerify, setStepVerify] = useState(false);

  const { put, loading } = useApi<{ email: string | undefined; phoneNumber: string | undefined }>(
    'resetPassword',
  );

  const form = useForm<ResetPasswordForm>({
    initialValues: {
      email: email ?? '',
      phoneNumber: '',
    },
    validate: {
      phoneNumber: (value) => {
        if (value && value.replace(/\D/g, '').length < 11) {
          return 'O telefone deve incluir o DDD  (exemplo: (11) 99999-9999).';
        }
        return undefined;
      },
    },
    validateInputOnBlur: true,
  });

  const [errorNode, setErrorNode] = useState<ReactNode>();

  const handleSubmit = async (values: ResetPasswordForm) => {
    setErrorNode(undefined);

    const email = values.email || undefined;
    const phoneNumber = values.phoneNumber || undefined;

    put<{ email: string; phoneNumber: string }>(
      { email: email, phoneNumber: phoneNumber },
      {
        error: {
          message: 'Falha ao recuperar senha',
          showNotification: false,
        },
        onSuccess: (response) => {
          const res = response as unknown as Response;
          form.reset();
          notifications.show({
            title: 'Instruções enviadas',
            message: `As instruções de recuperação de senha chegarão pelo WhatsApp final ${res.lastNumbers} ou pelo e-mail ${res.hiddenEmail}.`,
            color: 'green',
          });
          const url = new URL(window.location.href);
          url.searchParams.set('cod', 'true');
          if (email) {
            url.searchParams.set('email', email);
          }
          router.replace(url.toString());
          setStepVerify(true);
        },
        onError: (error) => {
          setErrorNode(
            <Alert
              color="red"
              radius="md"
              p="sm"
              classNames={{ message: 'text-center text-white' }}
            >
              {error?.status === 404
                ? 'Usuário não encontrado'
                : error?.status === 400
                  ? 'Número de telefone digitado incorretamente. Certifique-se de incluir o DDI e o DDD (exemplo: 5511999999999).'
                  : 'Ocorreu um erro ao recuperar a senha. Tente novamente'}
            </Alert>,
          );
        },
      },
    );
  };

  useEffect(() => {
    const cod = searchParams?.get('cod');
    if (cod === 'true') {
      setStepVerify(true);
    }
  }, [searchParams]);

  const backUrl = email ? `/entrar?email=${encodeURIComponent(email)}` : '/entrar';

  return (
    <>
      {!stepVerify ? (
        <form className="flex h-full flex-col text-white" onSubmit={form.onSubmit(handleSubmit)}>
          <div>
            <Title order={1} ta="center" mb="sm">
              Recuperar senha
            </Title>
            <Text ta="center" mb="lg">
              Informe seu e-mail ou telefone para recuperar sua senha. Você pode preencher ambos,
              mas ao menos um é obrigatório.
            </Text>
          </div>
          <div className="flex flex-col gap-4 animate-[fade-in_0.5s_ease-in]">
            <FloatingLabelTextInput
              {...customInputProps}
              {...form.getInputProps('email')}
              type="email"
              label="E-mail"
              autoComplete="on"
            />

            <PhoneInputWithLabel
              label="Telefone"
              {...form.getInputProps('phoneNumber')}
              onChange={({ phone }) => {
                form.getInputProps('phoneNumber').onChange(phone);
              }}
              className="w-full"
              ableCountryCode={false}
              customInputProps={customInputProps}
            />

            {errorNode}

            <Group justify="space-between" align="center" mt="md">
              <Button
                component={Link}
                variant="subtle"
                color="white"
                leftSection={<IconArrowLeft size={18} stroke={1.5} />}
                href={backUrl}
              >
                Voltar
              </Button>

              <Button
                type="submit"
                variant="filled"
                loading={loading}
                disabled={!form.values.email && !form.values.phoneNumber}
              >
                Recuperar Conta
              </Button>
            </Group>
          </div>
        </form>
      ) : (
        <NewPassword />
      )}
    </>
  );
}
